import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

/* ----------------------------- IFRAME CARD URL STORE ----------------------------- */
/* 
use for storing with limited time the iframe URL
*/

interface CardUrl {
  url: string
  expiry: number
}

interface UrlSusceptiveCardState {
  virtualCardUrl: CardUrl | null
  physicalCardUrl: CardUrl | null
  currentUuid: string | null
}

type UrlSusceptiveCardAction = {
  updateUuid: (uuid: string) => void
  updateVirtualCardUrl: (url: string | null) => void
  updatePhysicalCardUrl: (url: string | null) => void
  getUrlWithExpirationDate: (type: 'VIRTUAL' | 'PHYSICAL') => null | string
}

function addExpirationDate(url: string | null) {
  let item: CardUrl = null

  if (url) {
    const now = new Date()

    item = {
      url,
      // add 5 minutes
      expiry: now.getTime() + 300000
    }
  }

  return item
}

export const useCardIframeUrl = create<
  UrlSusceptiveCardState & UrlSusceptiveCardAction
>()(
  persist(
    (set, get) => ({
      virtualCardUrl: null,
      physicalCardUrl: null,
      // use to get iframe url
      currentUuid: null,
      updateUuid: (uuid: string) => set(() => ({ currentUuid: uuid })),
      updateVirtualCardUrl: (url: string | null) => {
        const item = addExpirationDate(url)
        return set(() => ({ virtualCardUrl: item }))
      },
      updatePhysicalCardUrl: (url: string | null) => {
        const item = addExpirationDate(url)
        return set(() => ({ physicalCardUrl: item }))
      },
      getUrlWithExpirationDate: (type: 'VIRTUAL' | 'PHYSICAL') => {
        const item =
          type === 'VIRTUAL' ? get().virtualCardUrl : get().physicalCardUrl

        if (!item) return null

        const now = new Date()

        if (now.getTime() > item.expiry) {
          type === 'VIRTUAL'
            ? set(() => ({ virtualCardUrl: null }))
            : set(() => ({ physicalCardUrl: null }))

          return null
        }

        return item.url
      }
    }),
    {
      name: 'card-iframe-url',
      storage: createJSONStorage(() => sessionStorage)
    }
  )
)

/* ----------------------------- DATA TOGGLE STORE ----------------------------- */
/* 
use for storing the current state of the show card information togge
*/

type DataToggleState = {
  virtualCardIsHidden: boolean
  physicalCardIsHidden: boolean
}

type DataToggleAction = {
  updateIsHidden: ({
    state,
    type
  }: {
    state: boolean
    type: 'PHYSICAL' | 'VIRTUAL'
  }) => void
}

export const useDataToggleStore = create<DataToggleState & DataToggleAction>()(
  persist(
    (set) => ({
      virtualCardIsHidden: true,
      physicalCardIsHidden: true,
      updateIsHidden: ({ state, type }) => {
        if (type === 'VIRTUAL') {
          set(() => ({ virtualCardIsHidden: state }))
        } else {
          set(() => ({ physicalCardIsHidden: state }))
        }
      }
    }),
    {
      name: 'card-data-toggle-state',
      storage: createJSONStorage(() => sessionStorage)
    }
  )
)

/* ----------------------------- LIMIT PHYSICAL CARD STORE ----------------------------- */
/* 
use for storing the current minimum balance required to request a physical card
*/

type LimitPhysicalCardState = {
  canRequestCard: boolean
  minBalance: number
}

type LimitPhysicalCardAction = {
  updateInfo: ({ canRequestCard, minBalance }: LimitPhysicalCardState) => void
}

export const useLimitPhysicalCardStore = create<
  LimitPhysicalCardState & LimitPhysicalCardAction
>()(
  persist(
    (set) => ({
      canRequestCard: false,
      minBalance: 20000,
      updateInfo: ({ canRequestCard, minBalance }) => {
        set(() => ({ canRequestCard }))
        set(() => ({ minBalance }))
      }
    }),
    {
      name: 'limit-physical-state',
      storage: createJSONStorage(() => sessionStorage)
    }
  )
)
